import { useState } from 'react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Box, Flex, Text } from '@chakra-ui/react';

import { useCreateAdminUser } from '@/features/admin-users/api/adminUserQueries';
import {
  AdminUserForm,
  type AdminUserFormValues,
} from '@/features/admin-users/components/AdminUserForm';
import { ApiError } from '@/lib/api-client';
import { sendPasswordResetEmail } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';

export const Route = createFileRoute('/_authenticated/users/new')({
  component: UsersNewPage,
});

function UsersNewPage() {
  const navigate = useNavigate();
  const createMutation = useCreateAdminUser();
  const [apiError, setApiError] = useState<string | undefined>(undefined);

  const handleSubmit = async (values: AdminUserFormValues) => {
    setApiError(undefined);

    // 1. 管理者ユーザーを作成
    let created;
    try {
      created = await createMutation.mutateAsync(values);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setApiError(body?.error ?? '管理者ユーザーの作成に失敗しました');
      } else {
        setApiError('管理者ユーザーの作成に失敗しました');
      }
      return;
    }

    // 2. 招待メール（初回パスワード設定リンク）を Identity Platform 標準メールで送信
    //    ユーザー作成は成功済みのため、送信失敗時は一覧へ遷移せずその旨を表示する
    try {
      await sendPasswordResetEmail(created.user.email);
      navigate({ to: '/users' });
    } catch {
      setApiError(
        'ユーザーは作成されましたが、招待メールの送信に失敗しました。時間をおいて再度お試しください。'
      );
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">
          管理者ユーザー 新規作成
        </Text>
        <Button variant="outline" onClick={() => navigate({ to: '/users' })}>
          一覧に戻る
        </Button>
      </Flex>

      <Box bg="bg.panel" p={6} borderRadius="md" shadow="sm">
        <AdminUserForm onSubmit={handleSubmit} submitLabel="作成" apiError={apiError} />
      </Box>
    </Box>
  );
}
